import { useEffect, useState, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface AiPrompt {
  id: string;
  key: string;
  name: string;
  description: string | null;
  system_prompt: string;
  model: string;
  temperature: number;
  active: boolean;
  updated_at: string;
  current_version: number;
  updated_by: string | null;
}

interface PromptVersion {
  id: string;
  prompt_id: string;
  version: number;
  content: string;
  model: string | null;
  comment: string | null;
  created_by: string;
  created_at: string;
}

const MODEL_OPTIONS = [
  "gemini-2.5-flash-lite",
  "gemini-2.5-flash",
  "gemini-2.0-flash",
  "claude-sonnet-4-6",
  "claude-opus-4-6",
  "claude-haiku-4-5-20251001",
  "gpt-4o",
];

type SaveStatus = "idle" | "changed" | "saving" | "saved";

// ── Version history panel ────────────────────────────────────────────────────

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function VersionViewModal({
  version,
  onClose,
}: {
  version: PromptVersion;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              v{version.version}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {version.model ?? "—"} · {version.created_by} ·{" "}
              {new Date(version.created_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        </div>
        {version.comment && (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
            <p className="text-xs text-amber-800">
              <span className="font-medium">Note: </span>
              {version.comment}
            </p>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-5">
          <pre className="font-mono text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
            {version.content}
          </pre>
        </div>
      </div>
    </div>
  );
}

function VersionHistory({
  promptId,
  currentVersion,
  onRestore,
}: {
  promptId: string;
  currentVersion: number;
  onRestore: (content: string, model: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<PromptVersion | null>(null);
  const PAGE = 20;

  async function loadVersions(reset = false) {
    setLoading(true);
    const from = reset ? 0 : offset;
    const { data, error } = await supabase
      .from("ai_prompt_versions")
      .select("id, prompt_id, version, content, model, comment, created_by, created_at")
      .eq("prompt_id", promptId)
      .order("version", { ascending: false })
      .range(from, from + PAGE - 1);

    if (error) {
      toast.error("Could not load version history");
    } else {
      const rows = data ?? [];
      setVersions((prev) => (reset ? rows : [...prev, ...rows]));
      setOffset(from + rows.length);
      setHasMore(rows.length === PAGE);
    }
    setLoading(false);
  }

  function handleToggle() {
    if (!open && versions.length === 0) {
      void loadVersions(true);
    }
    setOpen((v) => !v);
  }

  // Reload when prompt changes
  useEffect(() => {
    setVersions([]);
    setOffset(0);
    setOpen(false);
  }, [promptId]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">
            Version history
          </span>
          <span className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
            v{currentVersion} current
          </span>
        </div>
        <span className="text-gray-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {loading && versions.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">
              Loading…
            </div>
          ) : versions.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">
              No history yet
            </div>
          ) : (
            <>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-5 py-2.5 font-medium text-gray-500 w-12">Ver.</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-36">Model</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500">Note</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-32">Saved by</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500 w-24">Date</th>
                    <th className="px-5 py-2.5 w-28" />
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr
                      key={v.id}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50/30 transition-colors"
                    >
                      <td className="px-5 py-2.5 font-mono font-semibold text-gray-700">
                        v{v.version}
                        {v.version === currentVersion - 1 && (
                          <span className="ml-1 text-[9px] text-orange-500 font-normal">(prev)</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-gray-500 truncate max-w-[144px]">
                        {v.model ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-gray-600 max-w-[200px]">
                        <span className="line-clamp-1">{v.comment ?? "—"}</span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-400 truncate max-w-[128px]">
                        {v.created_by}
                      </td>
                      <td
                        className="px-3 py-2.5 text-gray-400"
                        title={new Date(v.created_at).toLocaleString()}
                      >
                        {relativeTime(v.created_at)}
                      </td>
                      <td className="px-5 py-2.5">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => setViewingVersion(v)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              onRestore(v.content, v.model);
                              toast.info(
                                `v${v.version} loaded into editor — review and save with a comment`,
                                { duration: 4000 }
                              );
                            }}
                            className="text-xs font-medium text-orange-600 hover:text-orange-700 transition-colors"
                          >
                            Restore
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {hasMore && (
                <div className="px-5 py-3 border-t border-gray-100 text-center">
                  <button
                    onClick={() => void loadVersions()}
                    disabled={loading}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  >
                    {loading ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {viewingVersion && (
        <VersionViewModal
          version={viewingVersion}
          onClose={() => setViewingVersion(null)}
        />
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PromptEditor() {
  const [prompts, setPrompts] = useState<AiPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Edit state
  const [editPrompt, setEditPrompt] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editTemp, setEditTemp] = useState(0.7);
  const [editActive, setEditActive] = useState(true);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();

  // Snapshot to detect changes
  const [snapshot, setSnapshot] = useState({ prompt: "", model: "", temp: 0.7, active: true });

  useEffect(() => {
    loadPrompts();
  }, []);

  async function loadPrompts() {
    const { data, error } = await supabase
      .from("ai_prompts")
      .select("id, key, name, description, system_prompt, model, temperature, active, updated_at, current_version, updated_by")
      .order("key");
    if (error) {
      toast.error("Failed to load prompts");
      return;
    }
    setPrompts(data ?? []);
    setLoading(false);
  }

  function selectPrompt(key: string) {
    const p = prompts.find((x) => x.key === key);
    if (!p) return;
    setSelectedKey(key);
    setEditPrompt(p.system_prompt);
    setEditModel(p.model);
    setEditTemp(p.temperature);
    setEditActive(p.active);
    setSnapshot({ prompt: p.system_prompt, model: p.model, temp: p.temperature, active: p.active });
    setSaveStatus("idle");
    setComment("");
    setCommentError("");
  }

  const hasChanges =
    editPrompt !== snapshot.prompt ||
    editModel !== snapshot.model ||
    editTemp !== snapshot.temp ||
    editActive !== snapshot.active;

  useEffect(() => {
    if (hasChanges && saveStatus !== "saving") {
      setSaveStatus("changed");
    }
  }, [editPrompt, editModel, editTemp, editActive, hasChanges, saveStatus]);

  async function handleSave() {
    if (!selectedKey) return;

    // Comment validation
    if (comment.trim().length < 10) {
      setCommentError("Please describe what you changed (min 10 characters)");
      return;
    }
    setCommentError("");

    setSaveStatus("saving");

    const selected = prompts.find((p) => p.key === selectedKey);
    if (!selected) return;

    const { data: { user } } = await supabase.auth.getUser();
    const adminEmail = user?.email ?? "unknown";

    const { data: newVersion, error } = await supabase.rpc("save_prompt_version", {
      p_prompt_id:   selected.id,
      p_new_content: editPrompt,
      p_new_model:   editModel,
      p_comment:     comment.trim(),
      p_admin_email: adminEmail,
    });

    if (error) {
      toast.error("Failed to save — " + error.message);
      setSaveStatus("changed");
      return;
    }

    toast.success(`Saved as v${newVersion} — takes effect on next memory`);
    const now = new Date().toISOString();
    setSnapshot({ prompt: editPrompt, model: editModel, temp: editTemp, active: editActive });
    setPrompts((prev) =>
      prev.map((p) =>
        p.key === selectedKey
          ? {
              ...p,
              system_prompt: editPrompt,
              model: editModel,
              temperature: editTemp,
              active: editActive,
              updated_at: now,
              current_version: newVersion as number,
              updated_by: adminEmail,
            }
          : p
      )
    );

    // Also update active/temperature (not handled by RPC)
    await supabase
      .from("ai_prompts")
      .update({ temperature: editTemp, active: editActive })
      .eq("key", selectedKey);

    setComment("");
    setSaveStatus("saved");
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveStatus("idle"), 2000);
  }

  function handleRevert() {
    setEditPrompt(snapshot.prompt);
    setEditModel(snapshot.model);
    setEditTemp(snapshot.temp);
    setEditActive(snapshot.active);
    setComment("");
    setCommentError("");
    setSaveStatus("idle");
  }

  function handleRestore(content: string, model: string | null) {
    setEditPrompt(content);
    if (model) setEditModel(model);
    setSaveStatus("changed");
    setComment("");
    setCommentError("");
  }

  // Shorten model name for the list badge
  function modelBadge(model: string) {
    return model
      .replace("gemini-", "g-")
      .replace("claude-", "c-")
      .replace("-flash-lite", "-lite")
      .replace("-flash", "-flash")
      .replace("gpt-", "gpt-");
  }

  const selected = prompts.find((p) => p.key === selectedKey);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      {/* Left panel — prompt list */}
      <div className="w-[280px] shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Prompts ({prompts.length})
          </p>
          <div className="flex items-center gap-2 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />active</span>
            <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-gray-300 inline-block" />off</span>
          </div>
        </div>
        {prompts.map((p) => (
          <button
            key={p.key}
            onClick={() => selectPrompt(p.key)}
            className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${
              selectedKey === p.key
                ? "bg-orange-50 border-l-2 border-l-orange-500"
                : "hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className="mt-0.5 h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: p.active ? "#22c55e" : "#d1d5db" }}
              />
              <p className={`text-sm font-medium truncate ${p.active ? "text-gray-900" : "text-gray-400"}`}>
                {p.key}
              </p>
            </div>
            <div className="flex items-center justify-between mt-1 pl-4">
              <span className="text-[10px] font-mono text-gray-400">{modelBadge(p.model)}</span>
              <span className="text-[10px] text-gray-300 font-mono">v{p.current_version}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Right panel — editor */}
      <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200 bg-white">
          <h1 className="text-xl font-bold text-gray-900">AI Prompt Editor</h1>
          <p className="text-sm text-gray-500 mt-1">
            Edit AI prompts without redeployment. Every save creates a new version — nothing is overwritten.
          </p>
          <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
            Changes here affect all AI processing immediately. Test carefully before saving.
          </div>
        </div>

        {!selected ? (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            Select a prompt from the list
          </div>
        ) : (
          <div className="p-8 max-w-4xl space-y-5">
            {/* Key + active toggle */}
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Key
                </label>
                <div className="font-mono text-sm bg-gray-100 rounded px-3 py-2 text-gray-700">
                  {selected.key}
                </div>
                {selected.updated_by && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Last saved by {selected.updated_by} · {relativeTime(selected.updated_at)}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Active
                </label>
                <button
                  type="button"
                  onClick={() => setEditActive((v) => !v)}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 ${
                    editActive ? "bg-green-500" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                      editActive ? "translate-x-7" : "translate-x-1"
                    }`}
                  />
                </button>
                <p className="text-[10px] text-gray-400 mt-1 text-center">
                  {editActive ? "in use" : "disabled"}
                </p>
              </div>
            </div>

            {/* Model + Temperature row */}
            <div className="flex gap-6">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Model
                </label>
                <select
                  value={editModel}
                  onChange={(e) => setEditModel(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  {MODEL_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                  {!MODEL_OPTIONS.includes(editModel) && (
                    <option value={editModel}>{editModel} (current)</option>
                  )}
                </select>
              </div>
              <div className="w-48">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Temperature: {editTemp.toFixed(1)}
                </label>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={editTemp}
                  onChange={(e) => setEditTemp(parseFloat(e.target.value))}
                  className="w-full accent-orange-500 mt-2"
                />
              </div>
            </div>

            {/* System prompt textarea */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                System Prompt
              </label>
              <textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                className="w-full min-h-[400px] rounded-md border border-gray-300 bg-white px-4 py-3 font-mono text-sm leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                <span>{editPrompt.length} characters</span>
                <span>~{Math.ceil(editPrompt.length / 4)} tokens</span>
              </div>
            </div>

            {/* Comment field */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                What changed?
                <span className="ml-1 text-red-400 font-normal normal-case">required</span>
              </label>
              <input
                type="text"
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value);
                  if (commentError && e.target.value.trim().length >= 10) {
                    setCommentError("");
                  }
                }}
                placeholder="e.g. Strengthened Mori voice rules for Slovak"
                className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  commentError ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"
                }`}
              />
              {commentError ? (
                <p className="text-xs text-red-500 mt-1">{commentError}</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">
                  {comment.trim().length}/10 minimum · This note appears in version history
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={!hasChanges || saveStatus === "saving"}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveStatus === "saving" ? "Saving..." : `Save as v${selected.current_version + 1}`}
              </button>
              <button
                onClick={handleRevert}
                disabled={!hasChanges || saveStatus === "saving"}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Revert changes
              </button>
              <span className="text-sm text-gray-400 ml-2">
                {saveStatus === "changed" && "Unsaved changes"}
                {saveStatus === "saving" && "Saving..."}
                {saveStatus === "saved" && "Saved ✓"}
              </span>
            </div>

            {/* Version history */}
            <VersionHistory
              promptId={selected.id}
              currentVersion={selected.current_version}
              onRestore={handleRestore}
            />
          </div>
        )}
      </div>
    </div>
  );
}
